import { isoToLocalDate } from "@jugl-web/utils";
import {
  DateTimeLabelFormattingVariant,
  useFormattedDate,
} from "@jugl-web/utils/hooks/useFormattedDate";
import { forwardRef, useMemo } from "react";
import { TableCell, TableCellProps } from "../TableCell/TableCell";

export type TableDateCellProps = {
  date: Date | string | null | undefined;
  /**
   * The format of the date to display. Supported formats are:
   * - { type: "date"; variant?: DateTimeLabelFormattingVariant } -> will use standard date format used across the app
   * - { type: "datetime"; variant?: DateTimeLabelFormattingVariant } -> will use standard datetime format used across the app
   * - any other string -> any custom `format` accepted by `date-fns`
   * @default { type: "datetime" }
   */
  format?:
    | { type: "date"; variant?: DateTimeLabelFormattingVariant }
    | { type: "datetime"; variant?: DateTimeLabelFormattingVariant }
    | { type: "custom"; value: string };
} & Pick<
  TableCellProps,
  | "classNames"
  | "clipboardContent"
  | "clipboardSuccessMessage"
  | "endContent"
  | "isFocused"
  | "isHoverable"
  | "isDisabled"
  | "onClick"
>;

export const TableDateCell = forwardRef<HTMLDivElement, TableDateCellProps>(
  (
    {
      date,
      classNames,
      format,
      clipboardContent,
      clipboardSuccessMessage,
      endContent,
      isFocused,
      isHoverable,
      isDisabled,
      onClick,
    },
    ref
  ) => {
    const { formatDateLabel, formatDateTimeLabel, localeAwareFormat } =
      useFormattedDate();

    const dateObject = useMemo(() => {
      if (!date) return null;
      return typeof date === "string" ? isoToLocalDate(date) : date;
    }, [date]);

    const formattedDate = useMemo(() => {
      if (!dateObject) return "-";

      if (!format || format.type === "datetime") {
        return formatDateTimeLabel(dateObject, format?.variant);
      }

      if (format.type === "date") {
        return formatDateLabel(dateObject, format?.variant);
      }

      return localeAwareFormat(dateObject, format.value);
    }, [
      dateObject,
      format,
      formatDateLabel,
      formatDateTimeLabel,
      localeAwareFormat,
    ]);

    return (
      <TableCell
        ref={ref}
        clipboardContent={clipboardContent}
        clipboardSuccessMessage={clipboardSuccessMessage}
        classNames={classNames}
        endContent={endContent}
        isFocused={isFocused}
        isHoverable={isHoverable}
        isDisabled={isDisabled}
        onClick={onClick}
      >
        {formattedDate}
      </TableCell>
    );
  }
);
